import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, FileText, Cloud, Search, ChevronRight, Folder, Radio, Plus } from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import ingestionService from './api';
import SourceCard from './components/SourceCard';
import SourceTypeFilter from './components/SourceTypeFilter';

const sourceTypeConfig = {
  database: {
    icon: Database,
    label: 'Databases',
    color: 'text-primaryDark',
    bgColor: 'bg-primaryLight',
    borderColor: 'border-primaryLight',
  },
  file: {
    icon: FileText,
    label: 'Files & Folders',
    color: 'text-primaryDark',
    bgColor: 'bg-PrimaryLight',
    borderColor: 'border-PrimaryLight',
  },
  saas: {
    icon: Cloud,
    label: 'SaaS Applications',
    color: 'text-primaryDark',
    bgColor: 'bg-primaryLight',
    borderColor: 'border-primaryLight',
  },
  streaming: {
    icon: Radio,
    label: 'Streaming',
    color: 'text-primaryDark',
    bgColor: 'bg-primaryLight',
    borderColor: 'border-primaryLight',
  },
  unstructured: {
    icon: Folder,
    label: 'Other Sources',
    color: 'text-primaryDark',
    bgColor: 'bg-primaryLight',
    borderColor: 'border-primaryLight',
  },
};

const connectorTypeMap = {
  postgres: 'database',
  mysql: 'database',
  sqlserver: 'database',
  oracle: 'database',
  mongodb: 'database',
  salesforce: 'saas',
  hubspot: 'saas',
  jira: 'saas',
  servicenow: 'saas',
  s3: 'file',
  gcs: 'file',
  azure_blob: 'file',
  local_file: 'file',
  file_upload: 'file',
  csv: 'file',
  excel: 'file',
  json: 'file',
  parquet: 'file',
  kafka: 'streaming',
  kinesis: 'streaming',
  pulsar: 'streaming',
};

const AnalyzeData = ({ onTabSwitch }) => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, source: null });
  const [deleting, setDeleting] = useState(null);
  const [activeTab, setActiveTab] = useState('grouped');

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ingestionService.getSources();
      setSources(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  };

  const groupedSources = sources.reduce((acc, source) => {
    const type = connectorTypeMap[source.connector?.toLowerCase()] || 'unstructured';
    if (!acc[type]) acc[type] = [];
    acc[type].push(source);
    return acc;
  }, {});

  const filteredSources = sources.filter(source => {
    const matchesSearch =
      source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.connector.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType =
      selectedType === 'all' ||
      connectorTypeMap[source.connector?.toLowerCase()] === selectedType;
    return matchesSearch && matchesType;
  });

  const handleExploreSource = (source) => {
    navigate(`/explore?id=${source.id}`);
  };

  const handleEditSource = (source) => {
    navigate(`/sources/edit?id=${source.id}`);
  };

  const handleDeleteSource = (source) => {
    setDeleteDialog({ isOpen: true, source });
  };

  const handleQuerySource = (source) => {
    if (onTabSwitch) onTabSwitch('queryData', { sourceId: source.id });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.source) return;
    try {
      setDeleting(deleteDialog.source.id);
      await ingestionService.deleteSource(deleteDialog.source.id);
      setSources(prev => prev.filter(s => s.id !== deleteDialog.source.id));
      setDeleteDialog({ isOpen: false, source: null });
    } catch (err) {
      alert('Failed to delete source. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ isOpen: false, source: null });
  };

  if (loading && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading sources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Failed to Load Sources</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadSources}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20">
      {/* Header */}
      <div className="bg-gray-50 px-4 sm:px-6 py-4 rounded-xl">
        <div className="flex flex-wrap gap-2 justify-end">
            <button
              onClick={() => { if (onTabSwitch) onTabSwitch('ingestData'); }}
              className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Continue to Ingestion</span>
              <span className="sm:hidden">Ingest</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Search and filter bar */}
        <div className="mb-4">
          <SourceTypeFilter
            sourceTypeConfig={sourceTypeConfig}
            selectedType={selectedType}
            onSelectType={setSelectedType}
            action={
              <button
                onClick={() => navigate('/sources/new')}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded-md hover:bg-primaryDark text-sm font-medium whitespace-nowrap"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add New Source</span>
              </button>
            }
          />
        </div>
        <div className="flex-1 relative mb-8">
            <Search className="absolute left-3 top-1/2  transform -translate-y-1/2 h-4 w-4 text-gray-400 mb-4" />
            <input
              type="text"
              placeholder="Search sources by name or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 h-11 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            />
        </div>

        

        {/* Grouped view */}
        {activeTab === 'grouped' && (
          <div className="space-y-8">
            {Object.entries(groupedSources)
              .filter(([type]) => selectedType === 'all' || type === selectedType)
              .map(([type, typeSources]) => {
                const config = sourceTypeConfig[type] || sourceTypeConfig.unstructured;
                const Icon = config.icon;
                return (
                  <div key={type}>
                    <div className="flex items-center gap-3 mb-4">
                      <div>
                        <p className="ml-1 text-sm text-gray-500 mt-0.5">
                          {typeSources.length} source{typeSources.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {typeSources.map((source) => (
                        <SourceCard
                          key={source.id}
                          source={source}
                          config={config}
                          onExplore={handleExploreSource}
                          onEdit={handleEditSource}
                          onDelete={handleDeleteSource}
                          onQuery={handleQuerySource}
                          deleting={deleting}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* List view */}
        {activeTab === 'list' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSources.map((source) => {
              const type = connectorTypeMap[source.connector?.toLowerCase()] || 'unstructured';
              const config = sourceTypeConfig[type] || sourceTypeConfig.unstructured;
              return (
                <SourceCard
                  key={source.id}
                  source={source}
                  config={config}
                  onExplore={handleExploreSource}
                  onEdit={handleEditSource}
                  onDelete={handleDeleteSource}
                  onQuery={handleQuerySource}
                  deleting={deleting}
                />
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!loading && filteredSources.length === 0 && (
          <div className="text-center py-12">
            <Database className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No sources found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete Source</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete &quot;{deleteDialog.source?.name}&quot;? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyzeData;
